import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize';
import { Button } from '@openx/components-core/lib/Button/Button';
import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis';
import type { FilterConfig, FilterOption } from '@openx/types/chipFilters';
import type { KeyboardEvent, ReactNode, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getFilterOptionLabel } from './dealsMoreFiltersShared';

export interface DealsMoreFiltersAutocompleteFieldProps {
  filterConfig: FilterConfig;
  currentOption: FilterOption | null;
  selectedValues?: string[];
  inputValue?: string;
  options: FilterOption[];
  filterOptions?: (options: FilterOption[], params: { inputValue: string }) => FilterOption[];
  onChange: (_event: SyntheticEvent, option: FilterOption | string | null) => void;
  onInputChange: (event: SyntheticEvent, value: string) => void;
  onClearSelected: () => void;
  clearDisabled: boolean;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  freeSolo?: boolean;
  dataTest?: string;
  children?: ReactNode;
}

export function DealsMoreFiltersAutocompleteField({
  filterConfig,
  currentOption,
  selectedValues = [],
  inputValue,
  options,
  filterOptions,
  onChange,
  onInputChange,
  onClearSelected,
  clearDisabled,
  onBlur,
  onKeyDown,
  freeSolo,
  dataTest = 'deals-more-filters-section',
  children,
}: DealsMoreFiltersAutocompleteFieldProps) {
  const { t } = useTranslation();
  const renderMultiSelectOption = filterConfig.multiple
    ? (props, option: FilterOption) => {
        const optionValue = String(option.value ?? '');
        const isChecked = selectedValues.includes(optionValue);
        const { key, ...optionProps } = props;

        return (
          <Box
            component="li"
            key={key ?? optionValue}
            data-test="autocomplete-option"
            {...optionProps}
            sx={{
              alignItems: 'center',
              backgroundClip: 'padding-box',
              backgroundColor: theme => (isChecked ? theme.palette.primary.light : 'transparent'),
              borderBottom: theme => `2px solid ${theme.palette.background.paper}`,
              display: 'flex',
              minHeight: 28,
              px: 1.5,
              py: 0,
              '&:hover': {
                backgroundColor: theme => (isChecked ? theme.palette.primary.light : theme.palette.action.hover),
              },
            }}
          >
            <Checkbox aria-hidden checked={isChecked} size="small" tabIndex={-1} sx={{ flexShrink: 0, p: 0 }} />
            <Box
              sx={{
                color: isChecked ? 'text.primary' : 'text.secondary',
                fontSize: 13,
                fontWeight: isChecked ? 600 : 400,
                lineHeight: '28px',
                ml: 1.5,
                minWidth: 0,
                width: '100%',
              }}
            >
              <Ellipsis width="100%" tooltip tooltipPlacement="top-start">
                {getFilterOptionLabel(option)}
              </Ellipsis>
            </Box>
          </Box>
        );
      }
    : undefined;

  return (
    <Stack
      direction="column"
      data-test={dataTest}
      sx={{
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography component="label" variant="body1Bold">
          {filterConfig.name}
        </Typography>
        {!clearDisabled && (
          <Button variant="text" size="small" onClick={onClearSelected} sx={{ minWidth: 0, px: 0 }}>
            {t('Clear Selected')}
          </Button>
        )}
      </Box>

      <AutoCompleteVirtualize<FilterOption, false, false, true>
        data-test="filter-list"
        textFieldProps={{
          placeholder: filterConfig.searchPhrase ?? t('Select or search...'),
        }}
        inputValue={inputValue}
        freeSolo={freeSolo || undefined}
        options={options}
        noOptionsText={filterConfig.noOptionsText || t('No options')}
        onBlur={onBlur}
        getOptionLabel={getFilterOptionLabel}
        value={currentOption}
        loading={filterConfig.loading}
        disabled={!filterConfig.onInputChange && filterConfig.loading}
        disableCloseOnSelect={filterConfig.multiple}
        renderOption={renderMultiSelectOption}
        onChange={onChange}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        filterOptions={filterOptions}
        renderOptions={{
          dataTest: 'filter-item',
          getOptionValue: option => option.displayName || option.value.toString(),
          getPaddingLevel: option => (option.padding ? option.padding : 0),
          tooltip: true,
        }}
      />

      {children}
    </Stack>
  );
}
